
import React, { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { generateResponsiveUrls } from '@/lib/advanced-image-compression';
import { generateCDNUrl, CDNConfig, ImageTransformation, detectBestFormat } from '@/lib/cdn-integration';

interface BlurImageProps {
  src: string;
  alt: string;
  width?: number;
  height?: number;
  className?: string;
  placeholder?: string;
  priority?: boolean;
  isLcp?: boolean;
  onLoad?: () => void;
  onError?: () => void;
  blurDataURL?: string;
  cdnConfig?: CDNConfig;
  transformations?: ImageTransformation;
}

// Generate a simple blur placeholder from image URL
const generateBlurDataURL = (src: string): string => {
  // Create a simple gray blur placeholder
  const canvas = document.createElement('canvas');
  canvas.width = 40;
  canvas.height = 30;
  const ctx = canvas.getContext('2d');
  
  if (ctx) {
    // Create gradient for more realistic blur effect
    const gradient = ctx.createLinearGradient(0, 0, 40, 30);
    gradient.addColorStop(0, '#f3f4f6');
    gradient.addColorStop(0.5, '#e5e7eb');
    gradient.addColorStop(1, '#d1d5db');
    
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, 40, 30);
  }
  
  return canvas.toDataURL('image/jpeg', 0.1);
};

export const BlurImage: React.FC<BlurImageProps> = ({
  src,
  alt,
  width,
  height,
  className = '',
  placeholder = '/placeholder.svg',
  priority = false,
  isLcp = false,
  onLoad,
  onError,
  blurDataURL,
  cdnConfig,
  transformations = {}
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [imageSrc, setImageSrc] = useState(placeholder);
  const [blurPlaceholder, setBlurPlaceholder] = useState<string>('');
  const [bestFormat, setBestFormat] = useState<'avif' | 'webp' | 'jpeg'>('webp');

  // Detect best format
  useEffect(() => {
    setBestFormat(detectBestFormat());
  }, []);

  // Generate blur placeholder
  useEffect(() => {
    if (blurDataURL) {
      setBlurPlaceholder(blurDataURL);
    } else {
      setBlurPlaceholder(generateBlurDataURL(src));
    }
  }, [src, blurDataURL]);

  // Generate optimized image URLs
  const getOptimizedUrls = () => {
    if (!src) return { srcSet: placeholder, sizes: '100vw' };

    let optimizedSrc = src;
    if (cdnConfig) {
      optimizedSrc = generateCDNUrl(src, { format: bestFormat, ...transformations }, cdnConfig);
    }

    const { srcSet, sizes } = generateResponsiveUrls(optimizedSrc);
    return { srcSet, sizes, optimizedSrc };
  };

  useEffect(() => {
    if (priority || isLcp) {
      const { optimizedSrc } = getOptimizedUrls();
      setImageSrc(optimizedSrc);
      return;
    }

    // For non-priority images, load in background
    const img = new Image();
    
    img.onload = () => {
      const { optimizedSrc } = getOptimizedUrls();
      setImageSrc(optimizedSrc);
      setIsLoading(false);
      if (onLoad) onLoad();
    };
    
    img.onerror = () => {
      setHasError(true);
      setIsLoading(false);
      if (onError) onError();
    };
    
    const { optimizedSrc } = getOptimizedUrls();
    img.src = optimizedSrc;

    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [src, priority, isLcp, onLoad, onError, bestFormat, cdnConfig, transformations]);

  const { srcSet, sizes } = getOptimizedUrls();

  return (
    <div className={cn("relative overflow-hidden", className)}>
      {/* Blur placeholder background */}
      {isLoading && blurPlaceholder && (
        <div 
          className="absolute inset-0 transition-opacity duration-300"
          style={{
            backgroundImage: `url(${blurPlaceholder})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'blur(20px)',
            transform: 'scale(1.1)',
          }}
        />
      )}
      
      {/* Loading overlay */}
      {isLoading && (
        <div className="absolute inset-0 bg-gray-200 animate-pulse" />
      )}
      
      {/* Responsive image with format optimization */}
      <picture>
        {/* AVIF source for maximum compression */}
        {bestFormat === 'avif' && cdnConfig && (
          <source
            srcSet={generateCDNUrl(src, { ...transformations, format: 'avif' }, cdnConfig)}
            type="image/avif"
            sizes={sizes}
          />
        )}
        
        {/* WebP source */}
        <source
          srcSet={cdnConfig ? generateCDNUrl(src, { ...transformations, format: 'webp' }, cdnConfig) : srcSet}
          type="image/webp"
          sizes={sizes}
        />
        
        {/* Main image with responsive srcSet */}
        <img
          src={hasError ? placeholder : imageSrc}
          srcSet={srcSet}
          sizes={sizes}
          alt={alt}
          width={width}
          height={height}
          className={cn(
            "w-full h-full object-cover transition-opacity duration-500",
            isLoading ? "opacity-0" : "opacity-100"
          )}
          loading={priority || isLcp ? "eager" : "lazy"}
          decoding={priority || isLcp ? "sync" : "async"}
          fetchPriority={priority || isLcp ? "high" : "auto"}
          onLoad={() => {
            setIsLoading(false);
            if (onLoad) onLoad();
          }}
          onError={() => {
            setHasError(true);
            setIsLoading(false);
            if (onError) onError();
          }}
          data-lcp={isLcp ? "true" : undefined}
        />
      </picture>
    </div>
  );
};
